import { getCountryRequirements } from './countryRequirements';

function cleanLine(input: string): string {
  return (input || '').replace(/\u2060/g, '').trim();
}

function stripTrailingPunctuation(input: string): string {
  return (input || '').replace(/[.,;:]+$/g, '').trim();
}

function splitStreetAndNumberFallback(streetLine: string): { street: string; houseNumber: string; houseNumberExt?: string } {
  const trimmed = cleanLine(streetLine);
  const cleaned = stripTrailingPunctuation(trimmed);
  if (!cleaned) return { street: '', houseNumber: '' };

  const markerNumber = cleaned.match(
    /^(.+?)\s*(?:#|№|Nº|NO\.?|NUM\.?|NUMERO|NÚM\.?|NÚMERO)\s*(\d+[A-Za-z]?)\b\s*(.*)$/i
  );
  if (markerNumber) {
    const prefix = markerNumber[1].trim();
    const numberToken = markerNumber[2].trim();
    const rest = markerNumber[3].trim();
    const street = rest ? `${prefix}, ${rest}` : prefix;
    return { street, houseNumber: numberToken };
  }

  const leadingComma = cleaned.match(/^(\d+[A-Za-z]?)\s*,\s*(.+)$/);
  if (leadingComma) {
    return { street: leadingComma[2].trim(), houseNumber: leadingComma[1].trim() };
  }

  const leadingSlash = cleaned.match(/^(\d+)\s*([/-])\s*(\d+[A-Za-z]?)\s+(.+)$/);
  if (leadingSlash) {
    return {
      street: leadingSlash[4].trim(),
      houseNumber: leadingSlash[1].trim(),
      houseNumberExt: `${leadingSlash[2]}${leadingSlash[3]}`,
    };
  }

  const leading = cleaned.match(/^(\d+[A-Za-z]?)\s+(.+)$/);
  if (leading) {
    return { street: leading[2].trim(), houseNumber: leading[1].trim() };
  }

  const numberThenComma = cleaned.match(/^(.+?)\s+(\d+)\s*,\s*(.+)$/);
  if (numberThenComma) {
    return { street: `${numberThenComma[1].trim()}, ${numberThenComma[3].trim()}`, houseNumber: numberThenComma[2].trim() };
  }

  const esFloorSuffix = cleaned.match(/^(.+?)\s+(\d+)\s+(\d+\s*[ºª°]\s*[A-Za-z0-9].*)$/);
  if (esFloorSuffix) {
    return {
      street: esFloorSuffix[1].trim(),
      houseNumber: esFloorSuffix[2].trim(),
      houseNumberExt: esFloorSuffix[3].replace(/\s+/g, '').trim(),
    };
  }

  const commaSlash = cleaned.match(/^(.+?),\s*(\d+)\s*([/-])\s*(\d+[A-Za-z]?)\s*$/);
  if (commaSlash) {
    return {
      street: commaSlash[1].trim(),
      houseNumber: commaSlash[2].trim(),
      houseNumberExt: `${commaSlash[3]}${commaSlash[4]}`,
    };
  }

  const comma = cleaned.match(/^(.+?),\s*(\d+)\s*([A-Za-z]?\d*[A-Za-z]*)?\s*$/);
  if (comma) {
    return { street: comma[1].trim(), houseNumber: comma[2].trim(), houseNumberExt: comma[3]?.trim() || undefined };
  }

  const trailingSlash = cleaned.match(/^(.+?)\s+(\d+)\s*([/-])\s*(\d+[A-Za-z]?)\s*$/);
  if (trailingSlash) {
    return {
      street: trailingSlash[1].trim(),
      houseNumber: trailingSlash[2].trim(),
      houseNumberExt: `${trailingSlash[3]}${trailingSlash[4]}`,
    };
  }

  const trailing = cleaned.match(/^(.+?)\s+(\d+)\s*([A-Za-z]?\d*[A-Za-z]*)?\s*$/);
  if (trailing) {
    return { street: trailing[1].trim(), houseNumber: trailing[2].trim(), houseNumberExt: trailing[3]?.trim() || undefined };
  }

  return { street: cleaned, houseNumber: '' };
}

function extractHouseNumberFromLine(line: string): string {
  const cleaned = cleanLine(line);
  if (!cleaned) return '';

  const slash = cleaned.match(/^(\d+)\s*[/-]\s*(\d+[A-Za-z]?)\s*$/);
  if (slash) return slash[1].trim();

  const simple = cleaned.match(/^(\d+)\s*([A-Za-z]?\d*[A-Za-z]*)?\s*$/);
  if (simple) return simple[1].trim();

  return '';
}

export interface AddressValidationResult {
  valid: boolean;
  houseNumberDetected: boolean;
  errors: string[];
}

export function validateShippingAddress(
  address: { address1?: string; address2?: string; city?: string; zip?: string; country_code?: string; country?: string } | null
): AddressValidationResult {
  if (!address) return { valid: false, houseNumberDetected: false, errors: ['Shipping address is missing'] };

  const countryCode = (address.country_code || address.country || 'NL').toUpperCase().replace('UK', 'GB');
  const req = getCountryRequirements(countryCode);

  const address1 = (address.address1 || '').trim();
  const address2 = (address.address2 || '').trim();
  const errors: string[] = [];

  if (!address1) {
    return { valid: false, houseNumberDetected: false, errors: ['Street address is missing'] };
  }

  let houseNumber = '';
  const primary = splitStreetAndNumberFallback(address1);
  houseNumber = primary.houseNumber;

  if (!houseNumber && address2) {
    const combined = splitStreetAndNumberFallback(`${address1} ${address2}`);
    if (combined.houseNumber) {
      houseNumber = combined.houseNumber;
    } else {
      houseNumber = extractHouseNumberFromLine(address2);
    }
  }

  if (req.houseNumberRequired && !houseNumber) {
    errors.push('House number not detected in address');
  }

  return {
    valid: errors.length === 0,
    houseNumberDetected: !!houseNumber,
    errors,
  };
}
